import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePet } from '../contexts/PetContext';
import { Heart, Sparkles, Check } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChoosePetPage() {
  const [petTypes, setPetTypes] = useState({});
  const [selectedPet, setSelectedPet] = useState(null);
  const [petName, setPetName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { createPet, hasPet } = usePet();
  const navigate = useNavigate();

  useEffect(() => {
    if (hasPet) {
      navigate('/dashboard');
      return;
    }
    
    const fetchPetTypes = async () => {
      try {
        const { data } = await axios.get(`${API}/pets/types`, { withCredentials: true });
        setPetTypes(data);
      } catch (e) {
        console.error('Failed to fetch pet types:', e);
      }
    };
    fetchPetTypes();
  }, [hasPet, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPet || !petName.trim()) {
      setError('Please choose a pet and give it a name!');
      return;
    }
    
    setLoading(true);
    setError('');
    
    const result = await createPet(selectedPet, petName.trim());
    setLoading(false);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
  };

  const petOptions = [
    { key: 'capybara', emoji: '🦫', bg: '#B9FBC0' },
    { key: 'red_panda', emoji: '🐼', bg: '#FFC8DD' },
    { key: 'frog', emoji: '🐸', bg: '#A2D2FF' },
    { key: 'corgi', emoji: '🐕', bg: '#FDF0D5' },
  ];

  return (
    <div className="min-h-screen bg-[#FFFAF0] p-4 py-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#CDB4DB] border-4 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] mb-4">
            <Heart className="w-10 h-10 text-black" strokeWidth={2.5} />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold font-['Fredoka'] text-gray-900 mb-2">
            Choose Your Companion!
          </h1>
          <p className="text-lg text-gray-600 font-['Nunito']">
            This cute friend will cheer you on as you complete tasks and focus sessions
          </p>
        </div>

        {/* Pet Selection Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {petOptions.map((pet) => {
            const petInfo = petTypes[pet.key];
            return (
              <button
                key={pet.key}
                onClick={() => setSelectedPet(pet.key)}
                className={`neo-card p-4 text-center transition-all ${
                  selectedPet === pet.key 
                    ? 'ring-4 ring-black scale-105' 
                    : 'hover:scale-102'
                }`}
                style={{ backgroundColor: pet.bg }}
                data-testid={`pet-option-${pet.key}`}
              >
                {selectedPet === pet.key && (
                  <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black text-white flex items-center justify-center">
                    <Check className="w-4 h-4" />
                  </div>
                )}
                {petInfo?.image ? (
                  <div className="w-24 h-24 mx-auto mb-3 rounded-full border-4 border-black overflow-hidden shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]">
                    <img 
                      src={petInfo.image} 
                      alt={petInfo.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="text-5xl mb-3">{pet.emoji}</div>
                )}
                <h3 className="font-['Fredoka'] font-bold text-lg">
                  {petInfo?.name || pet.key.replace('_', ' ')}
                </h3>
              </button>
            );
          })}
        </div>

        {/* Name Input Form */}
        <div className="neo-card p-8 max-w-md mx-auto">
          {error && (
            <div className="mb-4 p-3 bg-[#FF6B6B] text-white rounded-lg border-2 border-black font-semibold" data-testid="choose-pet-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-bold mb-2 font-['Fredoka']" htmlFor="petName">
                Name Your Pet
              </label>
              <input
                type="text"
                id="petName"
                value={petName}
                onChange={(e) => setPetName(e.target.value)}
                className="neo-input w-full"
                placeholder="Give your companion a cute name!"
                required
                data-testid="pet-name-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading || !selectedPet}
              className={`neo-btn w-full py-3 text-black flex items-center justify-center gap-2 ${
                selectedPet ? 'bg-[#B9FBC0]' : 'bg-gray-200 cursor-not-allowed'
              }`}
              data-testid="adopt-pet-btn"
            >
              {loading ? (
                <span>Adopting...</span>
              ) : (
                <>
                  <Heart className="w-5 h-5" />
                  <span>Adopt My Companion!</span>
                </>
              )}
            </button>
          </form>
        </div>

        {/* Fun decoration */}
        <div className="flex justify-center gap-4 mt-8">
          <Sparkles className="w-6 h-6 text-[#FFC8DD]" />
          <Sparkles className="w-6 h-6 text-[#B9FBC0]" />
          <Sparkles className="w-6 h-6 text-[#CDB4DB]" />
        </div>
      </div>
    </div>
  );
}
